import { DocumentAttachmentDetailDto } from "apis/data-contracts";
import { PDFDocument } from "pdf-lib";

type PdfAttachment = Pick<DocumentAttachmentDetailDto, "fileName" | "fileUrl">;

const PDF_FILE_EXTENSION_REGEX = /\.pdf$/i;

const isPdfAttachment = ({ fileName, fileUrl }: PdfAttachment): boolean => {
  if (fileName?.trim()) {
    return PDF_FILE_EXTENSION_REGEX.test(fileName.trim());
  }

  try {
    return PDF_FILE_EXTENSION_REGEX.test(new URL(fileUrl).pathname);
  } catch {
    return false;
  }
};

const appendPdfPages = async (
  mergedPdf: PDFDocument,
  pdfBytes: ArrayBuffer,
): Promise<void> => {
  const sourcePdf = await PDFDocument.load(pdfBytes, {
    ignoreEncryption: true,
  });

  const copiedPages = await mergedPdf.copyPages(
    sourcePdf,
    sourcePdf.getPageIndices(),
  );

  copiedPages.forEach((page) => {
    mergedPdf.addPage(page);
  });
};

export const mergePdfBlobWithAttachments = async (
  basePdfBlob: Blob,
  attachments: PdfAttachment[] = [],
): Promise<Blob> => {
  const pdfAttachments = attachments.filter(
    (attachment) => attachment.fileUrl && isPdfAttachment(attachment),
  );

  if (pdfAttachments.length === 0) {
    return basePdfBlob;
  }

  const mergedPdf = await PDFDocument.create();
  await appendPdfPages(mergedPdf, await basePdfBlob.arrayBuffer());

  for (const attachment of pdfAttachments) {
    try {
      const response = await fetch(attachment.fileUrl);

      if (!response.ok) {
        throw new Error(`failed to fetch attachment pdf: ${response.status}`);
      }

      await appendPdfPages(mergedPdf, await response.arrayBuffer());
    } catch (error) {
      console.warn(
        "Failed to merge attachment pdf:",
        attachment.fileName,
        error,
      );
    }
  }

  const mergedPdfBytes = await mergedPdf.save();

  return new Blob([mergedPdfBytes], {
    type: "application/pdf",
  });
};
